import { Injectable } from '@angular/core';
import { ApiServiceBase } from '@api/abstracts';
import { GalleryLayout14 } from '@components/gallery/components/gallery-layout-14/model';
import { Promotion } from '@components/promotion/models';
import { Store } from '@components/stores-locator/model';
import {
  GeoFilterOptions,
  LocationFilter,
  PromotionDocument,
  StoreQuery,
  StoreScheduleDetails,
} from '@services/elastic-search/models';
import { ElasticSearchResponse } from '@services/elastic-search/models/elastic-search-response';
import { StoreDocument } from '@services/elastic-search/models/store-document';
import {
  GET_GENERAL_PROMOTION_QUERY,
  GET_STORE_PROMOTION_QUERY,
  GET_STORE_SCHEDULE_QUERY,
  GET_STORES_BY_NAME_QUERY,
  GET_STORES_NEAR_COORDINATES_QUERY,
  GET_STORES_QUERY,
} from '@services/elastic-search/queries';
import {
  generalPromotionSerializer,
  storePromotionSerializer,
  storesSerializer,
} from '@services/elastic-search/serializers';
import { storeScheduleDetailsSerializer } from '@services/elastic-search/serializers/store-schedule-details-serializer';
import { objectToHashKey } from '@shared/utils';
import { map, Observable, switchMap } from 'rxjs';

// Nearest-stores selection policy (shared by the store locator and the appointment form)
const MIN_STORES = 5; // Minimum number of stores to display
const INCLUDE_ALL_STORES_WITHIN_RADIUS_KM = 50; // Stores within this radius are all returned
const MAX_SEARCH_RADIUS_KM = 1000; // Fallback radius used to guarantee MIN_STORES results
// A store this close behind the previous one joins the selection, and the one after is measured
// from it in turn, so a cluster straddling the cut-off is kept whole. 5 km and not 10: at 10 the
// chain crosses the gap between built-up areas and runs on, offering a shop 237 km away for Tours.
const CHAINING_MARGIN_KM = 5;
// The chain alone reaches 23 stores for Rouen, hence this cap. It never trims what was selected.
const MAX_STORES = 10;

@Injectable({
  providedIn: 'root',
})
export class ElasticSearchService extends ApiServiceBase<unknown> {
  constructor() {
    super('elasticsearch?networkId=280', 'elasticsearch', 'fournierWebservice');
  }

  getStores(query: StoreQuery = {}): Observable<Store[]> {
    return objectToHashKey(query).pipe(
      switchMap((hashKey) =>
        this.readElastic<ElasticSearchResponse<StoreDocument>>(GET_STORES_QUERY(query), {
          cacheExpirationTime: 3_600,
          forcedKey: `es_getStores_${hashKey}`,
        })
      ),
      map((stores) => storesSerializer(stores))
    );
  }

  getStoresByLocation(locationFilter: LocationFilter, geoFilterOptions?: GeoFilterOptions): Observable<Store[]> {
    const hashQuery = { ...locationFilter, ...geoFilterOptions };
    return objectToHashKey(hashQuery).pipe(
      switchMap((hashKey) =>
        this.readElastic<ElasticSearchResponse<StoreDocument>>(
          GET_STORES_NEAR_COORDINATES_QUERY(locationFilter, geoFilterOptions),
          {
            cacheExpirationTime: 3_600,
            forcedKey: `es_getStores_${hashKey}`,
          }
        )
      ),
      map((stores) => storesSerializer(stores, true))
    );
  }

  /**
   * Returns the stores to display near a location, applying the shared selection rule:
   * all stores within INCLUDE_ALL_STORES_WITHIN_RADIUS_KM, or the MIN_STORES closest ones
   * (searched up to MAX_SEARCH_RADIUS_KM) when fewer than MIN_STORES are nearby, then extended
   * store by store while each is within CHAINING_MARGIN_KM of the previous, up to MAX_STORES.
   */
  getNearestStores(locationFilter: LocationFilter): Observable<Store[]> {
    return this.getStoresByLocation(locationFilter, { sort: 'asc', searchRadiusKm: MAX_SEARCH_RADIUS_KM }).pipe(
      map((stores) => {
        const storesWithinRadius = stores.filter(
          (store) => store.distance !== null && store.distance <= INCLUDE_ALL_STORES_WITHIN_RADIUS_KM
        );
        const selected = storesWithinRadius.length >= MIN_STORES ? storesWithinRadius : stores.slice(0, MIN_STORES);

        // stores is sorted by ascending distance, and selected is one of its prefixes
        let end = selected.length;
        const maxEnd = Math.min(stores.length, Math.max(selected.length, MAX_STORES));
        while (end < maxEnd) {
          const previous = stores[end - 1]?.distance ?? null;
          const next = stores[end]?.distance ?? null;
          if (previous === null || next === null || next - previous > CHAINING_MARGIN_KM) break;
          end++;
        }
        return stores.slice(0, end);
      })
    );
  }

  getStoresByName(searchTerm: string, includeClosedStores = false): Observable<Store[]> {
    return this.readElastic<ElasticSearchResponse<StoreDocument>>(
      GET_STORES_BY_NAME_QUERY(searchTerm, includeClosedStores),
      {
        cacheExpirationTime: 60,
        forcedKey: `es_getStoresByName_${searchTerm}_${includeClosedStores}`,
      }
    ).pipe(map((stores) => storesSerializer(stores)));
  }

  getGeneralPromotion(): Observable<Promotion | undefined> {
    return this.readElastic<ElasticSearchResponse<PromotionDocument>>(GET_GENERAL_PROMOTION_QUERY, {
      cacheExpirationTime: 600,
      forcedKey: 'es_getGeneralPromotion',
    }).pipe(map(generalPromotionSerializer));
  }

  getStorePromotion(storeUuid: string): Observable<GalleryLayout14 | undefined> {
    return this.readElastic<ElasticSearchResponse<PromotionDocument>>(GET_STORE_PROMOTION_QUERY(storeUuid), {
      cacheExpirationTime: 600,
      forcedKey: `es_getStorePromotion_${storeUuid}`,
    }).pipe(map(storePromotionSerializer));
  }

  getStoreScheduleDetails(storeUuid: string): Observable<StoreScheduleDetails> {
    return this.readElastic<ElasticSearchResponse<StoreDocument>>(GET_STORE_SCHEDULE_QUERY(storeUuid), {
      cacheExpirationTime: 600,
      forcedKey: `es_getStoreScheduleDetails_${storeUuid}`,
    }).pipe(map(storeScheduleDetailsSerializer));
  }
}
